import { useState, useRef, useEffect, Suspense } from "react";
import { PremiumNav } from "@/components/landing/premium/PremiumNav";
import { PremiumHero } from "@/components/landing/premium/PremiumHero";
import { lazyWithRecovery } from "@/lib/lazyWithRecovery";

// Below-fold sections — lazy-loaded with recovery
const TrustStrip = lazyWithRecovery("TrustStrip", () => import("@/components/landing/premium/TrustStrip").then(m => ({ default: m.TrustStrip })));
const EcosystemSection = lazyWithRecovery("EcosystemSection", () => import("@/components/landing/premium/EcosystemSection").then(m => ({ default: m.EcosystemSection })));
const DetailedFeaturesSection = lazyWithRecovery("DetailedFeaturesSection", () => import("@/components/landing/premium/DetailedFeaturesSection").then(m => ({ default: m.DetailedFeaturesSection })));
const IndustriesSection = lazyWithRecovery("IndustriesSection", () => import("@/components/landing/premium/IndustriesSection").then(m => ({ default: m.IndustriesSection })));
const AIIntelligenceSection = lazyWithRecovery("AIIntelligenceSection", () => import("@/components/landing/premium/AIIntelligenceSection").then(m => ({ default: m.AIIntelligenceSection })));
const HospitalitySection = lazyWithRecovery("HospitalitySection", () => import("@/components/landing/premium/HospitalitySection").then(m => ({ default: m.HospitalitySection })));
const FinanceSection = lazyWithRecovery("FinanceSection", () => import("@/components/landing/premium/FinanceSection").then(m => ({ default: m.FinanceSection })));
const SecuritySection = lazyWithRecovery("SecuritySection", () => import("@/components/landing/premium/SecuritySection").then(m => ({ default: m.SecuritySection })));
const IntegrationsSection = lazyWithRecovery("IntegrationsSection", () => import("@/components/landing/premium/IntegrationsSection").then(m => ({ default: m.IntegrationsSection })));
const ComparisonSection = lazyWithRecovery("ComparisonSection", () => import("@/components/landing/premium/ComparisonSection").then(m => ({ default: m.ComparisonSection })));
const ExecutiveCTA = lazyWithRecovery("ExecutiveCTA", () => import("@/components/landing/premium/ExecutiveCTA").then(m => ({ default: m.ExecutiveCTA })));
const PremiumFooter = lazyWithRecovery("PremiumFooter", () => import("@/components/landing/premium/PremiumFooter").then(m => ({ default: m.PremiumFooter })));
const LeadCaptureForm = lazyWithRecovery("LeadCaptureForm", () => import("@/components/landing/LeadCaptureForm").then(m => ({ default: m.LeadCaptureForm })));
const DemoBooking = lazyWithRecovery("DemoBooking", () => import("@/components/landing/DemoBooking").then(m => ({ default: m.DemoBooking })));

/**
 * Renders children only when the sentinel enters the viewport.
 * Uses a 400px rootMargin to start loading slightly before visible.
 */
function LazySection({ children, minHeight = "200px" }: { children: React.ReactNode; minHeight?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); io.disconnect(); } },
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} style={visible ? undefined : { minHeight }}>
      {visible ? (
        <Suspense fallback={null}>{children}</Suspense>
      ) : null}
    </div>
  );
}

import { useDeferredTranslation } from "@/hooks/useDeferredTranslation";
import { useSEO } from "@/hooks/useSEO";

export default function Landing() {
  const { t } = useDeferredTranslation();
  useSEO({
    title: "EvoHR | HR, Payroll & Workforce Management Platform",
    description: "EvoHR is an AI-powered HR, payroll, workforce, finance, and business management platform by EVOTAK Group Holding LLC.",
    canonical: "https://evohr.io/",
    ogType: "website",
  });
  const [demoOpen, setDemoOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);

  // If user arrives with auth tokens, redirect to /auth
  useEffect(() => {
    const hasAuthCallback =
      window.location.hash.includes('access_token') ||
      window.location.search.includes('code=');
    if (hasAuthCallback) {
      window.location.href = `/auth${window.location.search}${window.location.hash}`;
    }
  }, []);

  const trackingRef = useRef<{ sessionId: string; leadScore: number; temperature: string }>({
    sessionId: `vs_${Date.now()}`,
    leadScore: 0,
    temperature: "cold",
  });

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden public-page-gold">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md">
        {t('common.skipToMainContent', 'Skip to main content')}
      </a>
      <PremiumNav onRequestDemo={() => setDemoOpen(true)} />
      <main id="main-content" role="main">
        <PremiumHero onRequestDemo={() => setDemoOpen(true)} onExplore={() => document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" })} />
        
        {/* Near-fold: intersection-observer gated but with small rootMargin */}
        <LazySection minHeight="300px">
          <TrustStrip />
        </LazySection>
        <LazySection minHeight="300px">
          <EcosystemSection />
        </LazySection>

        {/* Below fold: intersection-observer gated */}
        <LazySection>
          <section id="solutions" aria-label="Solutions">
            <DetailedFeaturesSection />
          </section>
        </LazySection>
        <LazySection><IndustriesSection /></LazySection>
        <LazySection><AIIntelligenceSection /></LazySection>
        <LazySection><HospitalitySection /></LazySection>
        <LazySection><FinanceSection /></LazySection>
        <LazySection><SecuritySection /></LazySection>
        <LazySection><IntegrationsSection /></LazySection>
        <LazySection><ComparisonSection /></LazySection>
        <LazySection><ExecutiveCTA onRequestDemo={() => setDemoOpen(true)} /></LazySection>
      </main>
      <LazySection minHeight="100px">
        <PremiumFooter />
      </LazySection>

      {demoOpen && (
        <Suspense fallback={null}>
          <DemoBooking open={demoOpen} onOpenChange={setDemoOpen} sessionId={trackingRef.current.sessionId} />
        </Suspense>
      )}
      {leadOpen && (
        <Suspense fallback={null}>
          <LeadCaptureForm open={leadOpen} onOpenChange={setLeadOpen} sessionId={trackingRef.current.sessionId} leadScore={trackingRef.current.leadScore} temperature={trackingRef.current.temperature as any} />
        </Suspense>
      )}
    </div>
  );
}
